/**
 * Column sorting for the Resource Plan summary table. Pure, client-safe, no I/O.
 *
 * With no column chosen, the table keeps RP-15.1's order: Active pins to the top,
 * Superseded sinks to the bottom, everything else is most-recently-updated first. Once a
 * reader clicks a header, that column's order wins outright — they asked for it.
 *
 * Money, hours and margin cross the API as `numeric` STRINGS (see plan-format.ts). They
 * are parsed to numbers here for ORDERING only; nothing parsed here is ever displayed, so
 * float64 rounding cannot reach a figure a user reads.
 */

import type { ResourcePlanSummaryView } from "@/schemas/resource-plans";

export type PlanSortColumn =
  | "solution"
  | "client"
  | "name"
  | "author"
  | "pricingModel"
  | "start"
  | "end"
  | "totalHours"
  | "totalRevenue"
  | "margin"
  | "status"
  | "resourcingStatus"
  | "staffingNeed"
  | "updatedAt";

export type SortDirection = "asc" | "desc";

export interface PlanSort {
  column: PlanSortColumn;
  direction: SortDirection;
}

const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** Lifecycle order, so sorting by Status reads Draft → … → Superseded, not A→Z. */
const STATUS_RANK: Record<string, number> = {
  Draft: 0,
  Proposed: 1,
  SoftCommitted: 2,
  Won: 3,
  HandedOver: 4,
  Staffed: 5,
  Active: 6,
  Closed: 7,
  Lost: 8,
  Superseded: 9,
  Locked: 10,
};

/** Nulls sort LAST in both directions — resolved before `dir` is applied, so a
 * descending sort never floats the blanks to the top. */
function compareNullable<T>(
  a: T | null,
  b: T | null,
  dir: 1 | -1,
  compare: (x: T, y: T) => number,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return compare(a, b) * dir;
}

function toNumber(value: string | null): number | null {
  if (value === null) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function toTime(value: string): number | null {
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Resourcing-status order: Not set first, then In Review → Staffing → Staffed → Returned. Unlike
 * `compareNullable` (blanks last in both directions), "Not set" is a real rank here, not a
 * blank — it is the state every plan starts in, so it belongs at the front of an ascending
 * sort rather than being shoved to the end regardless of direction.
 */
const RESOURCING_STATUS_RANK: Record<string, number> = {
  InReview: 1,
  Staffing: 2,
  Staffed: 3,
  Returned: 4,
};

function resourcingRank(status: string | null | undefined): number {
  return status ? (RESOURCING_STATUS_RANK[status] ?? 0) : 0;
}

const byText = (x: string, y: string) => COLLATOR.compare(x, y);
const byNumber = (x: number, y: number) => x - y;

function compareBy(a: ResourcePlanSummaryView, b: ResourcePlanSummaryView, sort: PlanSort): number {
  const dir: 1 | -1 = sort.direction === "asc" ? 1 : -1;
  switch (sort.column) {
    case "solution":
      return compareNullable(a.solutionName, b.solutionName, dir, byText);
    // The Account / Solution column stacks both names, so within one Account the
    // Solutions read in order too.
    case "client":
      return (
        compareNullable(a.clientAccountName, b.clientAccountName, dir, byText) ||
        compareNullable(a.solutionName, b.solutionName, dir, byText)
      );
    case "name":
      return compareNullable(a.name, b.name, dir, byText);
    case "author":
      return compareNullable(a.ownerName, b.ownerName, dir, byText);
    case "pricingModel":
      return compareNullable(a.pricingModel, b.pricingModel, dir, byText);
    // Months are "YYYY-MM", so text order is date order. An open-ended plan (no End)
    // sorts last, like any other blank.
    case "start":
      return compareNullable(a.startMonth, b.startMonth, dir, byText);
    case "end":
      return compareNullable(a.endMonth, b.endMonth, dir, byText);
    case "totalHours":
      return compareNullable(toNumber(a.totalHours), toNumber(b.totalHours), dir, byNumber);
    case "totalRevenue":
      return compareNullable(toNumber(a.totalRevenue), toNumber(b.totalRevenue), dir, byNumber);
    case "margin":
      return compareNullable(
        toNumber(a.marginOfRecordPercent),
        toNumber(b.marginOfRecordPercent),
        dir,
        byNumber,
      );
    case "status":
      return compareNullable(
        STATUS_RANK[a.status] ?? null,
        STATUS_RANK[b.status] ?? null,
        dir,
        byNumber,
      );
    case "resourcingStatus":
      return (resourcingRank(a.resourcingStatus) - resourcingRank(b.resourcingStatus)) * dir;
    // Most urgent first when ascending. A plan with no open request, or open requests with
    // no priority, sorts last either way — it carries no urgency to rank.
    case "staffingNeed":
      return compareNullable(
        a.staffingNeed?.priority?.sortOrder ?? null,
        b.staffingNeed?.priority?.sortOrder ?? null,
        dir,
        byNumber,
      );
    case "updatedAt":
      return compareNullable(toTime(a.updatedAt), toTime(b.updatedAt), dir, byNumber);
  }
}

function defaultRank(status: string): number {
  if (status === "Active") return 0;
  if (status === "Superseded") return 2;
  return 1;
}

function compareDefault(a: ResourcePlanSummaryView, b: ResourcePlanSummaryView): number {
  const rankDiff = defaultRank(a.status) - defaultRank(b.status);
  if (rankDiff !== 0) return rankDiff;
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

/** `sort === null` is the RP-15.1 default order. Ties under a chosen column fall back to
 * the default order, so equal rows never shuffle between renders. */
export function sortPlans(
  plans: readonly ResourcePlanSummaryView[],
  sort: PlanSort | null,
): ResourcePlanSummaryView[] {
  return [...plans].sort((a, b) => {
    if (sort === null) return compareDefault(a, b);
    return compareBy(a, b, sort) || compareDefault(a, b);
  });
}

/** Click a new column → ascending; click the same column again → flip direction. */
export function nextPlanSort(prev: PlanSort | null, column: PlanSortColumn): PlanSort {
  if (prev?.column === column) {
    return { column, direction: prev.direction === "asc" ? "desc" : "asc" };
  }
  return { column, direction: "asc" };
}
